import { Button, Typography, Checkbox, colors } from '@sweep-io/sweep-design';
import { Stack, Popover, Box } from '@mui/material';
import usePopover from '../../../../hooks/usePopover';
import { useCustomColumnsTableContext } from './CustomColumnsTableContext';
import { useManipulateColumns } from './useManipulateColumns';
import { useCallback, useState } from 'react';
import { SearchInput } from '../../SearchInput';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { ButtonSize } from '@sweep-io/sweep-design/dist/components/Button/types';
import { EditListColumn } from '@sweep-io/sweep-design/dist/icons';

interface EditColumnsSelectorProps {
  buttonSize?: ButtonSize;
}

export const EditColumnsSelector = ({ buttonSize = 'small' }: EditColumnsSelectorProps) => {
  const [search, setSearch] = useState('');
  const { anchorElement, anchorRef, isPopoverOpen, closePopover, openPopover } = usePopover();
  const {
    filteredAndSortedColumns: filteredColumns,
    columns,
    tableColumnSettings,
    tableId,
  } = useCustomColumnsTableContext();
  const excludedColumns = tableColumnSettings?.find(
    (setting) => setting.tableId === tableId,
  )?.excludedColumns;
  const currentOrder = tableColumnSettings?.find((setting) => setting.tableId === tableId)?.order;
  const { addNotification } = useSweepNotifications();

  const _searchedColumns = search
    ? (columns?.filter((col) =>
        col.headerName?.toString().toLowerCase().includes(search.toLowerCase()),
      ) ?? [])
    : columns;

  // Order columns by currentOrder if it exists, excluded columns are at the end
  const orderedSearchedColumns = _searchedColumns
    ? [..._searchedColumns].sort((a, b) => {
        const isAExcluded = excludedColumns?.includes(a.field) ?? false;
        const isBExcluded = excludedColumns?.includes(b.field) ?? false;

        // Put excluded columns at the end
        if (isAExcluded && !isBExcluded) return 1;
        if (!isAExcluded && isBExcluded) return -1;

        // If both are excluded or both are included, sort by order
        const orderA = currentOrder?.[a.field] ?? Number.MAX_SAFE_INTEGER;
        const orderB = currentOrder?.[b.field] ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      })
    : _searchedColumns;

  const { setUserTableColumnsSettings } = useManipulateColumns(tableId);
  const isColumnChecked = useCallback(
    (columnName: string) => !!filteredColumns?.find((col) => col.field === columnName),
    [filteredColumns],
  );

  const onChange = useCallback(
    (columnName: string) => {
      const isChecked = isColumnChecked(columnName);
      const excludedColumns =
        tableColumnSettings?.find((setting) => setting.tableId === tableId)?.excludedColumns || [];

      const columnLeftName = columns
        ?.filter((col) => !excludedColumns.includes(col.field))
        .map((column) => column.field);

      const columnsLeft = columns.length - excludedColumns.length;

      if (columnsLeft === 1 && columnLeftName?.includes(columnName)) {
        addNotification({
          message: 'At least one column is required',
          variant: SweepNotificationVariant.Warning,
        });
        return;
      }

      const currentTableSettings = tableColumnSettings?.find(
        (setting) => setting.tableId === tableId,
      );

      if (!currentTableSettings) {
        setUserTableColumnsSettings([
          ...(tableColumnSettings ?? []),
          { tableId, excludedColumns: [columnName] },
        ]);
      } else {
        const newTableColumnSettings = (tableColumnSettings ?? []).map((setting) => {
          if (setting.tableId === tableId) {
            return {
              ...setting,
              excludedColumns: isChecked
                ? [...excludedColumns, columnName]
                : excludedColumns?.filter((col) => col !== columnName),
            };
          }
          return setting;
        });
        setUserTableColumnsSettings(newTableColumnSettings);
      }
    },
    [
      tableColumnSettings,
      tableId,
      columns,
      addNotification,
      setUserTableColumnsSettings,
      isColumnChecked,
    ],
  );

  return (
    <>
      <Stack alignItems="center" sx={{ '& > button': { gap: 0.5 } }}>
        <Button onClick={openPopover} ref={anchorRef} variant="flat-grey" size={buttonSize}>
          <EditListColumn size="medium" />
          Edit columns
        </Button>
      </Stack>

      <Popover
        open={isPopoverOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={anchorElement}
        onClose={closePopover}
      >
        <Stack direction="column" p={1} gap={1}>
          <SearchInput
            withFixedMagnifyingGlassIcon
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Stack direction="column" spacing={1} width={300}>
            {orderedSearchedColumns.map((column) => (
              <Box
                key={column.field}
                display="flex"
                alignItems="center"
                onClick={() => onChange(column.field)}
                gap={1}
                sx={{
                  padding: '8px 16px',
                  '&:hover': { background: colors.grey[100], cursor: 'pointer' },
                }}
              >
                <Checkbox
                  key={column.field}
                  value={column.field}
                  checked={isColumnChecked(column.field)}
                  onChange={() => onChange(column.field)}
                />
                <Typography variant="body">{column.headerName}</Typography>
              </Box>
            ))}
            {orderedSearchedColumns?.length === 0 && (
              <Stack alignItems="center" p={2}>
                <Typography variant="body" color={colors.grey[700]}>
                  No results found
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
